import { ApolloError, isApolloError } from '@apollo/client';
import {
  messageIdForPspRefusedErrors,
  messageIdMappingForDialogs,
} from 'features/payment/message-id-mappings';
import { CardTypes } from 'features/payment/types';
import { GraphQLError as GQLError } from 'graphql';

import { NonNullableObject } from '@rbi-ctg/frontend';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { filterMap } from 'utils/array';
import { GraphQLError } from 'utils/network';

import { GraphQLErrorCodes, IGraphqlErrorMap, paymentErrorModalMaps } from './types';

const isPaymentErrorCode = (code: string): code is keyof typeof paymentErrorModalMaps =>
  code in paymentErrorModalMaps;

const isDialogKey = (key: string): key is keyof typeof messageIdMappingForDialogs =>
  key in messageIdMappingForDialogs;

const isPspRefusedCode = (code: string): code is keyof typeof messageIdForPspRefusedErrors =>
  code in messageIdForPspRefusedErrors;

const mapErrorsWithCodes = filterMap<GQLError, NonNullableObject<GQLError>, IGQLParsedErrorCode>(
  err => !!err.extensions?.code,
  err =>
    ({
      errorCode: err.extensions.code,
      message: err.message,
      extensions: err.extensions || {},
    } as IGQLParsedErrorCode)
);

/**
 * Given a GraphQL error, parse all the GraphQL error codes.
 */
export function parseGraphQLErrorCodes(error: GraphQLError | ApolloError): IGQLParsedErrorCode[] {
  if (isApolloError(error)) {
    return mapErrorsWithCodes(error.graphQLErrors);
  }
  return mapErrorsWithCodes(error.originalError || []);
}

export const isPspRefusedError = (error: any) =>
  parseGraphQLErrorCodes(error)[0]?.extensions?.responseMessage || null;
/**
 * Given an array of graphql errors, do any errors have an extension with the given code
 *
 * @param graphQLErrors {GraphQLError[]} An array of Apollo GraphQL errors
 * @param code {GraphQLErrorCodes} The code
 */
export const graphQLErrorsIncludeExtensionCode = (
  graphQLErrors: ApolloError['graphQLErrors'] = [],
  code: GraphQLErrorCodes
) => graphQLErrors.some(graphQLError => graphQLError?.extensions?.code === code);

export interface IErrorDialog {
  title?: string;
  body?: string | React.ReactNode;
  link?: {
    key: string;
    to: string;
    text: string;
  };
  message: string;
}

export interface IGQLParsedErrorCode {
  errorCode: GraphQLErrorCodes;
  message?: string;
  extensions?: any;
}

/**
 * Given an error code from the CTG GQL api, return localized error dialog content.
 *
 * Returns `undefined` if we have no dialog defined for the given error code.
 * @param parsedError RBI CTG GraphQL error code
 * @param staticStrings Defined static strings
 */
export function getErrorDialogForGraphQLCode(
  parsedError: IGQLParsedErrorCode
): IErrorDialog | undefined {
  const { errorCode, message } = parsedError;

  if (!isPaymentErrorCode(errorCode)) {
    return;
  }
  const paymentModalKey = paymentErrorModalMaps[errorCode];
  if (!isDialogKey(paymentModalKey)) {
    return;
  }
  const errorDialog = messageIdMappingForDialogs[paymentModalKey];
  const { title, body, message: messageId } = errorDialog;
  if (errorCode === GraphQLErrorCodes.GRAPHQL_VALIDATION_FAILED && message) {
    return {
      title: t(title),
      body: message,
      message: t(messageId),
    };
  }

  return {
    title: t(title),
    body: t(body),
    message: t(messageId),
  };
}

/**
 * Given an error code from PSP Refused errors, return the dialog information
 *
 * Returns `undefined` if we have no dialog defined for the given error code.
 * @param pspErrorCode PSP error code
 */
export function getPspRefusedErrorDialog(pspErrorCode: string): IErrorDialog | undefined {
  if (!isPspRefusedCode(pspErrorCode)) {
    return;
  }
  const errorDialog = messageIdForPspRefusedErrors[pspErrorCode];
  const { title, message } = errorDialog;

  return {
    title: t(title),
    message: t(message),
  };
}

/**
 * Digital Wallet payment error modal key
 * Returns the key for the digital wallet payment error modal
 * @param fdAccountId
 * @param defaultMessage
 * @returns
 */
export const getAppleOrGooglePayError = (fdAccountId: string): TranslationKey | null => {
  if (!fdAccountId) {
    return null;
  }
  // Send custom  message if it's apple pay or google pay
  const messageMapping: Record<string, TranslationKey> = {
    [CardTypes.APPLE_PAY]: 'orderFailureMessageForApplePay',
    [CardTypes.GOOGLE_PAY]: 'orderFailureMessageForGooglePay',
  };

  return messageMapping[fdAccountId];
};

/**
 * Creates a dictionary from an array of gql errors using code as the lookup index
 * If a code is not supplied the key will be `GQL_ERROR_$index`
 *
 * @param {GQLError[]} gqlErrors
 *
 * @returns {IGraphqlErrorMap}
 */
export const createGqlErrorMap = (gqlErrors: GQLError[]): IGraphqlErrorMap =>
  gqlErrors.reduce((acc: Record<string, GQLError>, error, idx) => {
    const key = `${error.extensions?.code || 'GQL_ERROR'}_${idx}`;
    acc[key] = error;
    return acc;
  }, {});

/**
 * Converts a value to an Error object.
 * If the value is already an Error, it is returned as is.
 * Otherwise, a new Error is created with the string representation of the value.
 * @param maybeError The value to convert to an Error.
 * @returns An Error object.
 */
export function toError(maybeError: unknown): Error {
  if (maybeError instanceof Error) {
    return maybeError;
  }
  try {
    return new Error(JSON.stringify(maybeError));
  } catch (err) {
    return new Error(String(maybeError));
  }
}

export * from './types';
